import { useState } from 'react';
import { diningOptionService } from '../../services/api';
import dedupeByName from '../../utils/dedupeByName';

const useDiningSetup = (tenantConfig) => {
    const [diningOptions, setDiningOptions] = useState([]);
    const [diningOptionObjects, setDiningOptionObjects] = useState({});
    const [fullDiningOptions, setFullDiningOptions] = useState({});
    const [availableTableNumbers, setAvailableTableNumbers] = useState({});
    const [selectedDiningOption, setSelectedDiningOption] = useState(null);
    const [tableNumber, setTableNumber] = useState('');
    const [tableNumberConfirmed, setTableNumberConfirmed] = useState(false);
    const [tableViewMode, setTableViewMode] = useState('numbered');

    const processTableData = (rawDiningOptions, { tableOnly = false } = {}) => {
        const result = {};
        Object.entries(rawDiningOptions).forEach(([name, tc]) => {
            if (tc === 'random_number') {
                if (!tableOnly) result[name] = 'random_number';
                return;
            }
            if (Array.isArray(tc) && tc.length === 1 && tc[0] === 'random_number') {
                if (!tableOnly) result[name] = 'random_number';
                return;
            }
            if (Array.isArray(tc) && tc.length > 0 && typeof tc[0] === 'object' && tc[0].number !== undefined) {
                result[name] = tc.map(t => t.number);
            } else if (tc && typeof tc === 'object' && !Array.isArray(tc)) {
                result[name] = Object.keys(tc).map(Number).sort((a, b) => a - b);
            } else if (Array.isArray(tc) && tc.length > 0) {
                result[name] = tc;
            } else {
                if (!tableOnly) result[name] = tc;
            }
        });
        return result;
    };

    const loadDiningOptions = async ({ tableOnly = false } = {}) => {
        try {
            const options = dedupeByName(await diningOptionService.getAll());
            let activeOptions = options.filter(o => o.is_active);

            if (tableOnly) {
                activeOptions = activeOptions.filter(o => {
                    if (!o.tables_config) return false;
                    const tc = o.tables_config;
                    if (tc === 'random_number') return false;
                    if (Array.isArray(tc) && tc.length === 1 && tc[0] === 'random_number') return false;
                    if (Array.isArray(tc) && tc.length > 0) return true;
                    if (typeof tc === 'object' && !Array.isArray(tc) && Object.keys(tc).length > 0) return true;
                    return false;
                });
            }

            const rawDiningOptions = {};
            const diningObjMap = {};
            activeOptions.forEach(option => {
                rawDiningOptions[option.name] = option.tables_config || null;
                diningObjMap[option.name] = option;
            });

            setFullDiningOptions(rawDiningOptions);
            setDiningOptionObjects(diningObjMap);

            const simple = processTableData(rawDiningOptions, { tableOnly });
            setDiningOptions(Object.keys(simple).sort((a, b) => a.localeCompare(b)));
            setAvailableTableNumbers(simple);

            return { rawDiningOptions, diningObjMap, simple };
        } catch (e) {
            console.error('Failed to fetch dining options, falling back to config:', e);
            const raw = tenantConfig?.dining_options || {};
            setFullDiningOptions(raw);
            const simple = processTableData(raw, { tableOnly });
            setDiningOptions(Object.keys(simple).sort((a, b) => a.localeCompare(b)));
            setAvailableTableNumbers(simple);
            return { rawDiningOptions: raw, diningObjMap: {}, simple };
        }
    };

    const refreshDiningOptions = async ({ tableOnly = false } = {}) => {
        await loadDiningOptions({ tableOnly });
    };

    return {
        diningOptions,
        diningOptionObjects,
        fullDiningOptions,
        availableTableNumbers,
        selectedDiningOption, setSelectedDiningOption,
        tableNumber, setTableNumber,
        tableNumberConfirmed, setTableNumberConfirmed,
        tableViewMode, setTableViewMode,
        processTableData,
        loadDiningOptions,
        refreshDiningOptions,
    };
};

export default useDiningSetup;
